import { listDirNames, type VfsEntry, type VfsFile, VfsSession } from "./vfs.js";

const COREUTILS = new Set(["ls", "cd", "pwd", "cat", "ll", "echo"]);

interface LsOptions {
    long: boolean;
    showHidden: boolean;
    paths: string[];
}

function parseLsArgs(args: string[]): LsOptions {
    const options: LsOptions = { long: false, showHidden: false, paths: [] };

    for (const arg of args) {
        if (arg.startsWith("-") && arg.length > 1) {
            for (const flag of arg.slice(1)) {
                if (flag === "l") options.long = true;
                if (flag === "h") options.showHidden = true;
            }
            continue;
        }
        options.paths.push(arg);
    }

    return options;
}

function parseInput(input: string): { cmd: string; args: string[] } {
    const parts = input.trim().split(/\s+/);
    return { cmd: parts[0]!.toLowerCase(), args: parts.slice(1) };
}

function permissionDenied(path: string): string[] {
    return [`bash: cd: ${path}: Permission denied`];
}

function notFound(path: string): string[] {
    return [`bash: cd: ${path}: No such file or directory`];
}

function formatLsLong(name: string, file: VfsFile): string {
    const permissions = file.permissions ?? "-rw-r--r--";
    return `${permissions} 1 guest guest ${String(file.size).padStart(4)} ${file.mtime} ${name}`;
}

export type LsRow =
    | { type: "short"; names: string[] }
    | { type: "long"; text: string; name?: string };

export type LsDisplay =
    | { type: "error"; lines: string[] }
    | { type: "ok"; rows: LsRow[] };

function formatLsLongDir(name: string, dir?: { permissions?: string }): string {
    const permissions = dir?.permissions ?? "drwxr-xr-x";
    return `${permissions} 2 guest guest 4096 Jun 25 10:00 ${name}`;
}

function formatLsDirDisplay(entry: VfsEntry, long: boolean, showHidden: boolean): LsRow[] {
    if (entry.type !== "dir") return [];

    const names = listDirNames(entry.children, showHidden);
    if (!long) {
        return [{ type: "short", names }];
    }

    const rows: LsRow[] = [{ type: "long", text: `total ${names.length}` }];
    for (const name of names) {
        const child = entry.children[name]!;
        if (child.type === "file") {
            rows.push({
                type: "long",
                text: formatLsLong(name, child),
                name,
            });
        } else {
            rows.push({
                type: "long",
                text: formatLsLongDir(name, child),
            });
        }
    }
    return rows;
}

function runLsDisplay(args: string[], vfs: VfsSession): LsDisplay {
    const { long, showHidden, paths } = parseLsArgs(args);
    const targets = paths.length > 0 ? paths : ["."];
    const rows: LsRow[] = [];

    for (const target of targets) {
        const path = vfs.resolvePath(target);
        if (!vfs.isPathAllowed(path)) {
            return { type: "error", lines: [`ls: cannot access '${target}': Permission denied`] };
        }

        const entry = vfs.lookup(path);
        if (!entry) {
            return {
                type: "error",
                lines: [`ls: cannot access '${target}': No such file or directory`],
            };
        }

        if (entry.type === "file") {
            const name = target.split("/").pop() ?? target;
            if (long) {
                rows.push({
                    type: "long",
                    text: formatLsLong(name, entry),
                    name,
                });
            } else {
                rows.push({ type: "short", names: [name] });
            }
            continue;
        }

        rows.push(...formatLsDirDisplay(entry, long, showHidden));
    }

    return { type: "ok", rows };
}

function lsDisplayToLines(display: LsDisplay): string[] {
    if (display.type === "error") return display.lines;
    return display.rows.map((row) => {
        if (row.type === "short") return row.names.join("  ");
        return row.text;
    });
}

function runLs(args: string[], vfs: VfsSession): string[] {
    return lsDisplayToLines(runLsDisplay(args, vfs));
}

export function isLsCommand(input: string): boolean {
    const cmd = input.trim().split(/\s+/)[0]?.toLowerCase();
    return cmd === "ls" || cmd === "ll";
}

export function getLsDisplay(input: string, vfs: VfsSession): LsDisplay {
    const { cmd, args } = parseInput(input);
    if (cmd === "ll") return runLsDisplay(["-l", ...args], vfs);
    return runLsDisplay(args, vfs);
}

function runCd(args: string[], vfs: VfsSession): string[] {
    const target = args[0] ?? vfs.home;
    const path = vfs.resolvePath(target);

    if (!vfs.isPathAllowed(path)) {
        return permissionDenied(path);
    }

    const entry = vfs.lookup(path);
    if (!entry) {
        return notFound(path);
    }

    if (entry.type !== "dir") {
        const name = path.split("/").pop() ?? path;
        return [`bash: cd: ${name}: Not a directory`];
    }

    vfs.cwd = path;
    return [];
}

function runPwd(vfs: VfsSession): string[] {
    return [vfs.cwd];
}

function runCat(args: string[], vfs: VfsSession): string[] {
    if (args.length === 0) {
        return ["cat: missing file operand"];
    }

    const lines: string[] = [];

    for (const arg of args) {
        const path = vfs.resolvePath(arg);
        if (!vfs.isPathAllowed(path)) {
            return [`cat: ${arg}: Permission denied`];
        }

        const entry = vfs.lookup(path);
        if (!entry) {
            return [`cat: ${arg}: No such file or directory`];
        }

        if (entry.type !== "file") {
            return [`cat: ${arg}: Is a directory`];
        }

        if (entry.imageSrc) {
            continue;
        }

        if (entry.downloadSrc) {
            if (lines.length > 0) lines.push("");
            lines.push(`cat: ${arg}: Binary file (standard input)`);
            continue;
        }

        if (lines.length > 0) lines.push("");
        lines.push(...entry.content.split("\n"));
    }

    return lines;
}

function expandEchoEscapes(text: string): string {
    return text
        .replace(/\\n/g, "\n")
        .replace(/\\t/g, "\t")
        .replace(/\\r/g, "\r")
        .replace(/\\\\/g, "\\");
}

function parseEchoInput(input: string): { text: string; expandEscapes: boolean } {
    const match = input.trim().match(/^echo(?:\s+([\s\S]*))?$/i);
    if (!match) {
        return { text: "", expandEscapes: false };
    }

    let rest = match[1] ?? "";
    let expandEscapes = false;
    const parts: string[] = [];

    while (rest.length > 0) {
        rest = rest.trimStart();
        if (!rest) break;

        if (rest.startsWith("-")) {
            let index = 1;
            while (index < rest.length && rest[index] !== " " && rest[index] !== "\t") {
                const flag = rest[index]!;
                if (flag === "e") expandEscapes = true;
                index += 1;
            }
            rest = rest.slice(index);
            continue;
        }

        if (rest[0] === '"' || rest[0] === "'") {
            const quote = rest[0];
            let index = 1;
            let value = "";

            while (index < rest.length) {
                if (rest[index] === "\\" && quote === '"' && index + 1 < rest.length) {
                    value += rest[index + 1];
                    index += 2;
                    continue;
                }

                if (rest[index] === quote) {
                    parts.push(value);
                    rest = rest.slice(index + 1);
                    break;
                }

                value += rest[index];
                index += 1;
            }

            if (index >= rest.length) {
                parts.push(value);
                rest = "";
            }
            continue;
        }

        const token = rest.match(/^[^\s]+/);
        if (!token) break;
        parts.push(token[0]!);
        rest = rest.slice(token[0]!.length);
    }

    return { text: parts.join(" "), expandEscapes };
}

function runEcho(input: string): string[] {
    const { text, expandEscapes } = parseEchoInput(input);
    const output = expandEscapes ? expandEchoEscapes(text) : text;

    if (output.length === 0) {
        return [""];
    }

    if (output.includes("\n")) {
        return output.split("\n");
    }

    return [output];
}

export function isCoreutilsErrorOutput(lines: string[]): boolean {
    if (lines.length === 0) return false;

    const line = lines[0]!;
    return (
        /^(bash|ls|cat|cd):/.test(line) &&
        /(denied|not found|No such file|Not a directory|Is a directory|missing file|cannot access|command not found)/i.test(
            line,
        )
    );
}

export function isCoreutilsCommand(input: string): boolean {
    const cmd = input.trim().split(/\s+/)[0]?.toLowerCase();
    return COREUTILS.has(cmd ?? "");
}

export function runCoreutils(input: string, vfs: VfsSession): string[] {
    const { cmd, args } = parseInput(input);

    if (cmd === "ll") return runLs(["-l", ...args], vfs);
    if (cmd === "ls") return runLs(args, vfs);
    if (cmd === "cd") return runCd(args, vfs);
    if (cmd === "pwd") return runPwd(vfs);
    if (cmd === "cat") return runCat(args, vfs);
    if (cmd === "echo") return runEcho(input);

    return [`bash: ${cmd}: command not found`];
}

export interface CatImageResult {
    sources: string[];
    enhanced: boolean;
}

export function resolveCatImages(input: string, vfs: VfsSession): CatImageResult | null {
    let catInput = input.trim();
    let enhanced = false;

    const pipeMatch = catInput.match(/^(.+?)\|\s*enhance\s*$/i);
    if (pipeMatch) {
        enhanced = true;
        catInput = pipeMatch[1]!.trim();
    }

    const { cmd, args } = parseInput(catInput);
    if (cmd !== "cat" || args.length === 0) return null;

    const sources: string[] = [];
    for (const arg of args) {
        const path = vfs.resolvePath(arg);
        if (!vfs.isPathAllowed(path)) return null;
        const entry = vfs.lookup(path);
        if (!entry) return null;
        if (entry.type !== "file") return null;
        if (!entry.imageSrc) return null;
        sources.push(entry.imageSrc);
    }

    return { sources, enhanced };
}
